import { useState, useCallback, useEffect, useRef } from 'react'
import { useApiFetch, useApiMutation } from '../../hooks/useApiFetch.js'
import { useAdminStore } from '../../stores/adminStore.js'
import { useUIStore } from '../../stores/uiStore.js'
import { Button, Input, Select, Modal, Badge, Table, Pagination, Alert, type TableColumn } from '../../components/ui/index.js'
import { StatusBadge } from '../../components/admin/index.js'
import { formatDate, formatCredits, formatNumber } from '../../lib/format.js'
import styles from './AdminUsers.module.css'

interface AdminUser {
  id: string
  email: string
  name: string | null
  role: string
  premiumPlan: string
  aiCredits: number
  createdAt: string
  _count: { invitations: number; aiGenerations: number }
}

interface UsersResponse {
  users: AdminUser[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
}

type ActionType = 'grant_credits' | 'set_premium' | 'set_free' | 'set_admin' | 'set_user'

export function AdminUsers() {
  const { filters, setFilters, resetFilters } = useAdminStore()
  const { addToast } = useUIStore()
  const [planFilter, setPlanFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // 페이지 진입 시 필터 초기화 (다른 Admin 페이지의 상태가 남는 문제 방지)
  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [actionType, setActionType] = useState<ActionType>('grant_credits')
  const [creditAmount, setCreditAmount] = useState(10)

  const params = new URLSearchParams()
  params.set('page', String(filters.page))
  params.set('pageSize', String(filters.pageSize))
  params.set('sortBy', filters.sortBy)
  params.set('sortOrder', filters.sortOrder)
  if (filters.search) params.set('search', filters.search)
  if (planFilter) params.set('plan', planFilter)

  const { data, isLoading, error, refetch } = useApiFetch<UsersResponse>(
    `/api/admin/users?${params.toString()}`
  )

  const { trigger, isLoading: actionLoading } = useApiMutation('/api/admin/users')

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchInput(val)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setFilters({ search: val || undefined, page: 1 })
    }, 300)
  }, [setFilters])

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  const handleSort = useCallback((key: string, order: 'asc' | 'desc') => {
    setFilters({ sortBy: key, sortOrder: order })
  }, [setFilters])

  const handlePageChange = useCallback((page: number) => {
    setFilters({ page })
  }, [setFilters])

  const openActionModal = useCallback((user: AdminUser, action: ActionType) => {
    setSelectedUser(user)
    setActionType(action)
    setCreditAmount(10)
    setModalOpen(true)
  }, [])

  const executeAction = useCallback(async () => {
    if (!selectedUser) return

    const body: Record<string, unknown> = {
      action: actionType,
      userId: selectedUser.id,
    }
    if (actionType === 'grant_credits') {
      body.credits = creditAmount
    }

    const result = await trigger(body)
    if (result) {
      addToast({ type: 'success', message: '처리되었습니다.' })
      setModalOpen(false)
      refetch()
    } else {
      addToast({ type: 'error', message: '처리에 실패했습니다.' })
    }
  }, [selectedUser, actionType, creditAmount, trigger, addToast, refetch])

  const columns: TableColumn<AdminUser>[] = [
    { key: 'name', label: '이름', sortable: true, render: (row) => row.name ?? '-' },
    { key: 'email', label: '이메일', sortable: true },
    { key: 'role', label: '역할', render: (row) => <StatusBadge status={row.role} />, width: '80px' },
    { key: 'premiumPlan', label: '플랜', render: (row) => <StatusBadge status={row.premiumPlan} />, width: '90px' },
    { key: 'aiCredits', label: 'AI 크레딧', sortable: true, render: (row) => formatCredits(row.aiCredits), align: 'right' as const, width: '100px' },
    { key: 'invitations', label: '청첩장', render: (row) => formatNumber(row._count.invitations), align: 'right' as const, width: '80px' },
    { key: 'aiGenerations', label: 'AI 생성', render: (row) => formatNumber(row._count.aiGenerations), align: 'right' as const, width: '80px' },
    { key: 'createdAt', label: '가입일', sortable: true, render: (row) => formatDate(row.createdAt, 'yyyy.M.d'), width: '100px' },
    {
      key: 'actions',
      label: '액션',
      width: '120px',
      render: (row) => (
        <div className={styles.actionBtns}>
          <button
            className={styles.actionLink}
            onClick={(e) => { e.stopPropagation(); openActionModal(row, 'grant_credits') }}
          >
            크레딧
          </button>
          <button
            className={styles.actionLink}
            onClick={(e) => {
              e.stopPropagation()
              openActionModal(row, row.premiumPlan === 'PREMIUM' ? 'set_free' : 'set_premium')
            }}
          >
            {row.premiumPlan === 'PREMIUM' ? '무료' : '프리미엄'}
          </button>
          <button
            className={styles.actionLink}
            onClick={(e) => {
              e.stopPropagation()
              openActionModal(row, row.role === 'ADMIN' ? 'set_user' : 'set_admin')
            }}
          >
            {row.role === 'ADMIN' ? '일반' : '관리자'}
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.filters}>
        <Input
          size="sm"
          placeholder="이름 또는 이메일 검색..."
          value={searchInput}
          onChange={handleSearchChange}
          className={styles.searchInput}
        />
        <Select
          size="sm"
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); setFilters({ page: 1 }) }}
          options={[
            { value: '', label: '전체 플랜' },
            { value: 'FREE', label: 'FREE' },
            { value: 'PREMIUM', label: 'PREMIUM' },
          ]}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<AdminUser>
        columns={columns}
        data={data?.users ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={handlePageChange}
          size="sm"
        />
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="유저 액션" size="sm">
        {selectedUser && (
          <div className={styles.modalContent}>
            <p className={styles.modalUser}>{selectedUser.name ?? selectedUser.email}</p>

            {actionType === 'grant_credits' && (
              <Input
                label="크레딧 수량"
                type="number"
                value={String(creditAmount)}
                onChange={(e) => setCreditAmount(Number(e.target.value))}
              />
            )}
            {actionType === 'set_premium' && <p>이 유저를 <Badge color="blue">PREMIUM</Badge> 으로 변경합니다.</p>}
            {actionType === 'set_free' && <p>이 유저를 <Badge color="gray">FREE</Badge> 로 변경합니다.</p>}
            {actionType === 'set_admin' && <p>이 유저를 <Badge color="red">ADMIN</Badge> 으로 변경합니다.</p>}
            {actionType === 'set_user' && <p>이 유저를 <Badge color="gray">USER</Badge> 로 변경합니다.</p>}

            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>취소</Button>
              <Button loading={actionLoading} onClick={executeAction}>확인</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
